import React from 'react';
import PropTypes from 'prop-types';
import { isValid, parse } from 'date-fns';
import HubCardList from './HubCardList';
import CounselorInfo from './CounselorInfo';
import ObjectiveStatusAlert from './ObjectiveStatusAlert';

const objectiveStatuses = {
  achieved: {
    label: 'Achieved',
    sortOrder: 2,
    icon: 'check_circle',
    colorClass: 'vads-u-color--success-dark',
  },
  'in progress': {
    label: 'In Progress',
    sortOrder: 0,
    icon: 'flag',
    colorClass: 'vads-u-color--primary',
  },
  'closed/uncompleted': {
    label: 'Closed/Uncompleted',
    sortOrder: 3,
    icon: 'warning',
    colorClass: 'vads-u-color--warning-darker',
  },
  suspended: {
    label: 'Suspended',
    sortOrder: 1,
    icon: 'warning',
    colorClass: 'vads-u-color--warning-darker',
  },
};

const getObjectiveStatus = value =>
  objectiveStatuses[
    typeof value === 'string' ? value.trim().toLowerCase() : ''
  ];

const getEffectiveDate = value => {
  const dateValue = typeof value === 'string' ? value.trim() : '';
  if (!dateValue) return null;

  // These are calendar dates, so do not convert them through UTC.
  return (
    ["yyyy-MM-dd'Z'", 'yyyy-MM-dd', 'MM/dd/yyyy']
      .map(pattern => parse(dateValue, pattern, new Date(0)))
      .find(isValid) || null
  );
};

const getEffectiveDateSortValue = value =>
  getEffectiveDate(value)?.getTime() ?? Number.MIN_SAFE_INTEGER;

const planDataKeys = [
  'rehabilitationPlan',
  'rehabilitationPlanDetails',
  'rehabPlan',
];

const getPlanDetails = attributes =>
  planDataKeys.reduce(
    (planDetails, key) => planDetails || attributes?.[key],
    null,
  ) || {};

const hasValue = value => {
  if (Array.isArray(value)) return value.some(hasValue);

  return (
    value !== null &&
    value !== undefined &&
    (typeof value !== 'string' || value.trim() !== '')
  );
};

const displayValue = value => (hasValue(value) ? value : 'Not available');

const getFieldValue = (source, keys) =>
  keys.map(key => source?.[key]).find(hasValue);

const PlanField = ({ label, children, className = 'vads-u-margin-top--4' }) => {
  return (
    <div className={className}>
      <p className="vads-u-font-weight--bold vads-u-margin--0">{label}</p>
      <div className="vads-u-margin-top--2">{children}</div>
    </div>
  );
};

const ContactInfo = ({ contact = {} }) => {
  const name = getFieldValue(contact, ['name', 'fullName']);
  const email = getFieldValue(contact, ['email', 'emailAddress']);
  const phone = getFieldValue(contact, ['phone', 'phoneNumber']);

  return (
    <>
      <p className="vads-u-margin--0">Name: {displayValue(name)}</p>
      <p className="vads-u-margin--0 vads-u-margin-top--2">
        Email address: {displayValue(email)}
      </p>
      <p className="vads-u-margin--0 vads-u-margin-top--2">
        Telephone number: {displayValue(phone)}
      </p>
    </>
  );
};

const ObjectiveDetails = ({ objective }) => {
  const status = getObjectiveStatus(objective.status);
  const statusLabel = status?.label || displayValue(objective.status);
  const serviceProvider = getFieldValue(objective, [
    'serviceProvider',
    'serviceProviderName',
    'providerName',
  ]);
  const address =
    getFieldValue(objective, [
      'serviceProviderAddress',
      'providerAddress',
      'address',
    ]) ||
    [
      objective.serviceProviderAddressNoAndStreet,
      objective.serviceProviderAddressAptUnitNumber,
      [
        objective.serviceProviderAddressCity,
        objective.serviceProviderAddressState,
        objective.serviceProviderAddressZipCode,
        objective.serviceProviderAddressCountry,
      ]
        .filter(hasValue)
        .join(', '),
    ].filter(hasValue);
  const addressLines = (Array.isArray(address) ? address : [address]).filter(
    hasValue,
  );
  const pointOfContact = getFieldValue(objective, [
    'pointOfContact',
    'contact',
  ]) || {
    name: objective.serviceProviderPocName,
    email: objective.serviceProviderPocEmail,
    phone: getFieldValue(objective, [
      'serviceProviderPocPhone',
      'serviceProviderPocInternationalPhone',
    ]),
  };
  const duration =
    getFieldValue(objective, ['durationOfService', 'duration']) ??
    (hasValue(objective.fromDate) || hasValue(objective.toDate)
      ? `${displayValue(objective.fromDate)} - ${displayValue(
          objective.toDate,
        )}`
      : null);

  return (
    <va-accordion-item level={3} class="vads-u-margin-bottom--0p25">
      {status && (
        <va-icon
          slot="icon"
          icon={status.icon}
          aria-hidden="true"
          class={`${status.colorClass} vads-u-align-self--flex-start vads-u-flex--auto vads-u-margin-top--0p25 vads-u-margin-right--1`}
        />
      )}
      {/* The accordion copies this heading into its shadow root, so use inline styles. */}
      <h3 slot="headline">
        <span style={{ display: 'block' }}>{statusLabel}</span>
        <span
          style={{
            display: 'block',
            fontWeight: 'normal',
            marginTop: '4px',
            overflowWrap: 'anywhere',
          }}
        >
          <strong>Objective:</strong>{' '}
          {displayValue(getFieldValue(objective, ['objective', 'description']))}
        </span>
      </h3>
      <div className="vads-u-display--flex vads-u-flex-direction--column">
        <PlanField label="Services" className="vads-u-margin-top--0">
          <p className="vads-u-margin--0">{displayValue(objective.services)}</p>
        </PlanField>
        <PlanField label="Duration of service">
          <p className="vads-u-margin--0">{displayValue(duration)}</p>
        </PlanField>
        <PlanField label="Name of Service Provider">
          <p className="vads-u-margin--0">{displayValue(serviceProvider)}</p>
        </PlanField>
        <PlanField label="Address of Service Provider">
          {addressLines.length > 0 ? (
            addressLines.map((line, lineIndex) => (
              <p
                key={line}
                className={
                  lineIndex === 0
                    ? 'vads-u-margin--0'
                    : 'vads-u-margin--0 vads-u-margin-top--2'
                }
              >
                {line}
              </p>
            ))
          ) : (
            <p className="vads-u-margin--0">Not available</p>
          )}
        </PlanField>
        <PlanField label="Point of contact">
          <ContactInfo contact={pointOfContact} />
        </PlanField>
        <PlanField label="What will be expected">
          <p className="vads-u-margin--0">
            {displayValue(objective.evaluationCriteria)}
          </p>
        </PlanField>
        <PlanField label="How your progress will be checked">
          <p className="vads-u-margin--0">
            {displayValue(objective.evaluationProcedure)}
          </p>
        </PlanField>
        <PlanField label="How often reviews will occur">
          <p className="vads-u-margin--0">
            {displayValue(objective.evaluationSchedule)}
          </p>
        </PlanField>
      </div>
    </va-accordion-item>
  );
};

const RehabilitationPlanDetails = ({
  attributes = {},
  showHubCards = false,
}) => {
  const planDetails = getPlanDetails(attributes);
  const planObjectives = planDetails?.objectives || attributes?.objectives;
  const objectives = Array.isArray(planObjectives)
    ? planObjectives.filter(Boolean)
    : [];
  const sortedObjectives = objectives
    .map((objective, index) => ({ objective, index }))
    .sort(
      (first, second) =>
        (getObjectiveStatus(first.objective.status)?.sortOrder ?? 4) -
          (getObjectiveStatus(second.objective.status)?.sortOrder ?? 4) ||
        getEffectiveDateSortValue(second.objective.progressEffectiveDate) -
          getEffectiveDateSortValue(first.objective.progressEffectiveDate) ||
        first.index - second.index,
    );

  return (
    <>
      <ObjectiveStatusAlert
        caseId={attributes.resCaseId}
        planId={planDetails.rehabPlanId}
        objectives={objectives}
      />
      <div className="vads-u-margin-top--3">
        <h2 className="vads-u-font-size--h3 vads-u-font-family--sans">
          Rehabilitation plan details
        </h2>
        <p className="vads-u-margin-top--4">
          We’ve activated your Chapter 31 rehabilitation plan. Here’s an
          overview:
        </p>

        <ul>
          <li>Plan type: {displayValue(planDetails.planType)}</li>
          <li>Program goal: {displayValue(planDetails.programGoal)}</li>
          <li>
            Anticipated completion date:{' '}
            {displayValue(planDetails.anticipatedCompletionDate)}
          </li>
        </ul>
      </div>

      {objectives.length > 0 ? (
        <div className="vads-u-margin-top--4">
          <p>
            Your program objectives and details are in the accordions in the
            next section.
          </p>
          <va-accordion section-heading="Program objectives">
            {sortedObjectives.map(({ objective, index }) => (
              <ObjectiveDetails
                key={objective?.objectiveId || objective?.description || index}
                objective={objective}
              />
            ))}
          </va-accordion>
        </div>
      ) : (
        <p>Program objectives: Not available</p>
      )}

      <p className="vads-u-margin-top--4">
        If you have questions about your rehabilitation plan, contact your
        counselor.
      </p>

      <div className="vads-u-margin-top--4">
        <CounselorInfo />
      </div>

      {showHubCards && <HubCardList step={7} />}
    </>
  );
};

PlanField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
};

ContactInfo.propTypes = {
  contact: PropTypes.object,
};

ObjectiveDetails.propTypes = {
  objective: PropTypes.object.isRequired,
};

RehabilitationPlanDetails.propTypes = {
  attributes: PropTypes.object,
  showHubCards: PropTypes.bool,
};

export default RehabilitationPlanDetails;
